import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, X, Smartphone } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 5 seconds
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-6 right-6 z-50 max-w-md mx-auto animate-fadeIn">
      <Card className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-2xl border-none">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/80 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-7 w-7 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Install TurfSense App
            </h3>
            <p className="text-sm text-white/90 mb-3" style={{fontFamily: 'Inter, sans-serif'}}>
              {isIOS 
                ? 'Tap Share button, then "Add to Home Screen"'
                : 'Install our app for quick access and offline support'
              }
            </p>
            
            {!isIOS && (
              <Button
                onClick={handleInstallClick}
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Install Now
              </Button>
            )}
            
            {isIOS && (
              <div className="text-xs text-white/80 space-y-1">
                <p>1. Tap the Share icon (square with arrow)</p>
                <p>2. Scroll down and tap "Add to Home Screen"</p>
                <p>3. Tap "Add" to confirm</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
